import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

import { useAuthStore } from '@/stores/auth'

declare module 'vue-router' {
  interface RouteMeta {
    /** Shell to render around the page. */
    layout?: 'public' | 'owner' | 'bare'
    /** Presenter-only page. Backend authorization remains authoritative. */
    requiresAuth?: boolean
    /** Sign-in step for signed-out owners; signed-in owners skip to their surveys. */
    authFlow?: boolean
    title?: string
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/features/participation/LandingPage.vue'),
    meta: { layout: 'public', title: 'Join a survey' },
  },
  {
    path: '/s/:code',
    name: 'participate',
    component: () => import('@/features/participation/ParticipatePage.vue'),
    meta: { layout: 'bare', title: 'Survey' },
    props: true,
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/features/auth/LoginPage.vue'),
    meta: { layout: 'public', title: 'Presenter sign in', authFlow: true },
  },
  {
    path: '/login/new-password',
    name: 'new-password',
    component: () => import('@/features/auth/NewPasswordPage.vue'),
    meta: { layout: 'public', title: 'Choose your password', authFlow: true },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/features/auth/ForgotPasswordPage.vue'),
    meta: { layout: 'public', title: 'Reset your password', authFlow: true },
  },
  // Former email-link pages: Cognito sends codes, not links.
  { path: '/auth/callback', redirect: { name: 'login' } },
  { path: '/reset-password', redirect: { name: 'forgot-password' } },
  {
    path: '/account/password',
    name: 'change-password',
    component: () => import('@/features/auth/ChangePasswordPage.vue'),
    meta: { layout: 'owner', requiresAuth: true, title: 'Change password' },
  },
  {
    path: '/surveys',
    name: 'surveys',
    component: () => import('@/features/survey-setup/SurveyLibraryPage.vue'),
    meta: { layout: 'owner', requiresAuth: true, title: 'Surveys' },
  },
  {
    path: '/surveys/:surveyId/edit',
    name: 'survey-edit',
    component: () => import('@/features/survey-setup/SurveyEditorPage.vue'),
    meta: { layout: 'owner', requiresAuth: true, title: 'Edit survey' },
    props: true,
  },
  {
    path: '/surveys/:surveyId/runs',
    name: 'survey-runs',
    component: () => import('@/features/presenter/RunsPage.vue'),
    meta: { layout: 'owner', requiresAuth: true, title: 'Runs' },
    props: true,
  },
  {
    path: '/runs/:runId',
    name: 'run',
    component: () => import('@/features/presenter/RunDashboardPage.vue'),
    meta: { layout: 'owner', requiresAuth: true, title: 'Presenter dashboard' },
    props: true,
  },
  {
    path: '/runs/:runId/cards',
    name: 'run-cards',
    component: () => import('@/features/presenter/JoinCardsPage.vue'),
    meta: { layout: 'bare', requiresAuth: true, title: 'Join cards' },
    props: true,
  },
  {
    path: '/runs/:runId/present',
    name: 'present',
    component: () => import('@/features/presentation/PresentPage.vue'),
    meta: { layout: 'bare', requiresAuth: true, title: 'Presentation' },
    props: true,
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/features/NotFoundPage.vue'),
    meta: { layout: 'public', title: 'Not found' },
  },
]

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
})

router.beforeEach(async (to) => {
  // Participant pages never load the authentication client.
  if (!to.meta.requiresAuth && !to.meta.authFlow) return true
  const auth = useAuthStore()
  await auth.init()

  if (to.meta.requiresAuth && !auth.isSignedIn) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }
  if (to.meta.authFlow && auth.isSignedIn) return { name: 'surveys' }
  // The invitation step exists only between a temporary-password sign-in and its completion.
  if (to.name === 'new-password' && auth.status !== 'challenge') return { name: 'login' }
  return true
})

router.afterEach((to) => {
  document.title = to.meta.title ? `${to.meta.title} · Live Survey` : 'Live Survey'
})
